import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  Paper,
} from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Line,
} from "recharts";
import { WidgetContainer } from "../../shared/widgetContainer/widgetContainer";

import { useTheme } from "@mui/material/styles";

const METRICS = [
  { key: "storyPoints", value: "Story Points" },
  { key: "issuesResolved", value: "Issues Resolved" },
  { key: "averageCycleTime", value: "Avg Cycle Time" },
];

const COLORS = [
  "#70C930",
  "#F9CA00",
  "#BA1650",
  "#E97F03",
  "#9D419B",
  "#00C49F",
  "#0088FE",
  "#FFBB28",
];

const JiraPerformanceTrend = ({
  data = [],
  selectedMetric = "storyPoints",
  onMetricChange,
  draggable = false,
  editMode = false,
}) => {
  const [selectedTeams, setSelectedTeams] = useState([]);
  const theme = useTheme();
  useEffect(() => {
    if (data.length > 0) {
      setSelectedTeams(data.map((team) => team.team));
    }
  }, [data]);

  // Dynamically assign colors to teams and ensure they match between graph and checkboxes
  const TEAM_COLORS = useMemo(() => {
    const colors = {};
    data.forEach((teamData, i) => {
      colors[teamData.team] = COLORS[i % COLORS.length];
    });
    return colors;
  }, [data]);

  const handleTeamToggle = (team) => {
    setSelectedTeams((prev) =>
      prev.includes(team) ? prev.filter((t) => t !== team) : [...prev, team]
    );
  };

  const visibleData = data.filter((team) => selectedTeams.includes(team.team));

  const months = data[0]?.trend?.map((t) => t.month) || [];

  const averageLine = useMemo(() => {
    if (visibleData.length === 0) return [];

    return months.map((month, i) => {
      let total = 0;
      let count = 0;
      visibleData.forEach((team) => {
        const value = team.trend[i]?.[selectedMetric];
        if (typeof value === "number") {
          total += value;
          count += 1;
        }
      });
      return {
        month,
        avg: count ? Math.round(total / count) : 0,
      };
    });
  }, [visibleData, selectedMetric]);

  const CustomMonthYearTick = ({ x, y, payload, theme }) => {
    const [month, year] = (payload.value || "").split(" ");
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fontWeight={"bold"}
          fill={theme.palette.text.primary}
        >
          {month}
          <tspan x={0} dy={15} fontSize={12}>
            {year}
          </tspan>
        </text>
      </g>
    );
  };

  return (
    <WidgetContainer
      title="Team Performance Trend"
      description="12-month trends by team for velocity, delivery, and resolution time."
      loaded={data.length > 0}
      maximize
      download
      draggable={draggable}
      editMode={editMode}
    >
      <Box
        sx={{
          px: 2,
          pt: 1,
          pb: 2,
          display: "flex",
          gap: 2,
          height: "calc(100% - 60px)",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 0 }}>
            <ToggleButtonGroup
              value={selectedMetric}
              exclusive
              onChange={(e, val) => onMetricChange(val)}
              sx={{ mb: 2, height: "35px" }}
            >
              {METRICS.map((metric) => (
                <ToggleButton key={metric.key} value={metric.key}>
                  {metric.value}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <ResponsiveContainer width="100%" height={"100%"}>
            <AreaChart margin={{ top: 0, right: 0, left: -10, bottom: 15 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                stroke="#ccc"
                type="category"
                allowDuplicatedCategory={false}
                tickMargin={5}
                tick={<CustomMonthYearTick theme={theme} />}
              />
              <YAxis
                stroke="#ccc"
                tick={{ fontWeight: "bold", fill: theme.palette.text.primary }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: "#333", border: "none" }}
                labelStyle={{ color: "#ccc" }}
              />

              {visibleData.map((team, i) => (
                <Area
                  key={`${team.team}-${selectedMetric}`}
                  data={team.trend}
                  type="monotone"
                  dataKey={selectedMetric}
                  name={team.team}
                  stroke={TEAM_COLORS[team.team]}
                  fill={TEAM_COLORS[team.team]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}

              <Line
                data={averageLine}
                type="monotone"
                dataKey="avg"
                stroke="#ffffff"
                strokeDasharray="5 3"
                strokeWidth={2}
                name="Average"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        <Box
          sx={{
            minWidth: 180,
            height: "fit-content",
            overflowY: "auto",
            p: 2,
            pt: 1,
            mt: 6,

            backgroundColor: theme.palette.background.subBox,
            borderRadius: 2,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 2, position: "relative" }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 0,
                color: theme.palette.text.primary,
                fontWeight: "bold",
                fontSize: "14px",
                letterSpacing: "1px",
                textTransform: "uppercase", // Makes the text uppercase
                display: "inline-block",
                position: "relative",
              }}
            >
              Teams
            </Typography>
            <Box
              sx={{
                position: "absolute",
                bottom: -5,
                left: 0,
                width: "100%", // Full-width line
                height: "2px", // Thicker line
                backgroundColor: "#333", // Darker line color
              }}
            />
          </Box>

          {data.map((team) => {
            const isChecked = selectedTeams.includes(team.team);
            const teamColor = TEAM_COLORS[team.team];

            return (
              <FormControlLabel
                key={team.team}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={() => handleTeamToggle(team.team)}
                    sx={{
                      color: teamColor,
                      "&.Mui-checked": {
                        color: teamColor,
                      },
                    }}
                  />
                }
                label={team.team}
                sx={{
                  display: "block",
                  color: isChecked ? theme.palette.text.primary : "#aaa",
                  fontWeight: isChecked ? "bold" : "normal",
                  ml: 0,
                  letterSpacing: "1px", // Added letter spacing for team name
                  "&:hover": {
                    color: teamColor,
                  },
                  "& .MuiFormControlLabel-label": {
                    fontWeight: "bold", // Makes team name bold
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </WidgetContainer>
  );
};

export default React.memo(JiraPerformanceTrend);
